/**
 * Unfold Lite: the same game for low-budget phones (a Galaxy J7: eight slow cores, a single-core Mali, 2 GB), with the
 * heavy parts swapped out: no live pictures in the creator, every person on small textures, no anti-aliasing, the
 * barest graphics level (half the pixels, no shadows or effects, a fifth of the crowd, less drawn far off), 30 fps.
 * Same saves, same multiplayer, same everything else: only how it's drawn changes.
 *
 * On when: ?lite=1 (kept), a lite.* address, the setting (Settings → Unfold Lite), or, on a first visit with no
 * choice made yet, a phone that's plainly weak (little memory, or a budget graphics chip). ?lite=0 turns it off.
 */
const KEY = 'unfold.lite';
/** on by itself for weak phones: off until the owner has tried Lite on a budget phone (then true) */
const AUTO = true; // (tried on a Galaxy J7, 2026-10-25: smoother; shipped)
const AUTO_KEY = 'unfold.lite.auto';

function stored(): string | null { try { return localStorage.getItem(KEY); } catch { return null; } }
function store(v: '1' | '0') { try { localStorage.setItem(KEY, v); } catch { /* storage blocked */ } }

/** a phone that will struggle with the full game (checked before anything heavy loads) */
export function weakDevice(): boolean {
  const nav = navigator as Navigator & { deviceMemory?: number };
  const phone = typeof matchMedia !== 'undefined' && matchMedia('(pointer: coarse)').matches && Math.min(screen.width, screen.height) < 900;
  if (!phone) return false;
  if ((nav.deviceMemory ?? 8) <= 3) return true;
  // (the graphics chip, by name: the old Malis, Adreno 3xx–5xx, PowerVR)
  try {
    const gl = document.createElement('canvas').getContext('webgl'), dbg = gl?.getExtension('WEBGL_debug_renderer_info');
    const gpu = gl && dbg ? String(gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL)) : '';
    gl?.getExtension('WEBGL_lose_context')?.loseContext();
    return /Mali-(T\d|4\d\d|G5[12]|G31)|Adreno \(TM\) [3-5]\d\d|PowerVR|SGX/i.test(gpu);
  } catch { return false; }
}

function decide(): { on: boolean; auto: boolean } {
  const q = new URLSearchParams(location.search).get('lite');
  if (q === '1' || q === '0') { store(q); return { on: q === '1', auto: false }; }
  if (/^lite\./i.test(location.hostname)) return { on: true, auto: false };
  const s = stored();
  if (s === '1' || s === '0') return { on: s === '1', auto: false };
  // (first visit, no choice yet: a weak phone starts in Lite, and is told so once)
  if (AUTO && weakDevice()) { store('1'); try { localStorage.setItem(AUTO_KEY, '1'); } catch { /* */ } return { on: true, auto: true }; }
  return { on: false, auto: false };
}

const d = typeof window === 'undefined' ? { on: false, auto: false } : decide();
/** Lite is on for this visit */
export const LITE = d.on;
/** Lite came on by itself on this phone (say so once, with how to turn it off) */
export function liteAutoNote(): boolean { try { if (localStorage.getItem(AUTO_KEY) !== '1') return false; localStorage.removeItem(AUTO_KEY); return true; } catch { return false; } }
/** turn Lite on or off (takes effect on the reload) */
export function setLite(on: boolean) { store(on ? '1' : '0'); }
